import React, { useEffect, useRef } from "react";
import feather from "feather-icons";
import Chart from "chart.js/auto";

const Tokenomics = () => {
  const chartRef = useRef(null);

  useEffect(() => {
    setTimeout(() => feather.replace(), 200);

    if (!chartRef.current) return;
    const ctx = chartRef.current.getContext("2d");

    const data = {
      labels: [
        "Public Sale",
        "Team",
        "Advisors",
        "Community",
        "Ecosystem",
        "Liquidity",
        "DAO Reserve",
      ],
      datasets: [
        {
          data: [30, 15, 5, 25, 12, 8, 5],
          backgroundColor: [
            "#ec4899",
            "#3b82f6",
            "#c026d3",
            "#f43f5e",
            "#8b5cf6",
            "#14b8a6",
            "#f97316",
          ],
          borderWidth: 0,
        },
      ],
    };

    const config = {
      type: "doughnut",
      data,
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            position: "right",
            labels: { color: "#fff", font: { size: 12 } },
          },
          tooltip: {
            callbacks: {
              label: (ctx) =>
                `${ctx.label}: ${ctx.raw}% (${(
                  ctx.raw * 10000000
                ).toLocaleString()} NEXORA)`,
            },
          },
        },
        cutout: "65%",
      },
    };

    const chartInstance = new Chart(ctx, config);
    return () => chartInstance.destroy();
  }, []);

  return (
    <div className="text-white min-h-screen container mx-auto px-4 py-16 max-w-6xl">
      {/* === Header === */}
      <div className="text-center mb-16">
        <h1 className="text-5xl font-bold mb-6 text-transparent bg-clip-text bg-gradient-to-r from-neonPink to-neonBlue">
          NEXORA Tokenomics
        </h1>
        <p className="text-xl max-w-3xl mx-auto text-gray-300">
          A balanced and transparent token distribution model designed to
          sustain long-term ecosystem growth, reward active contributors,
          and empower the NEXORA community globally.
        </p>
      </div>

      {/* === Main Stats === */}
      <div className="bg-gray-900/80 backdrop-blur-lg rounded-xl p-8 mb-16 border border-neonPurple/40 shadow-lg shadow-neonPurple/10">
        <div className="grid md:grid-cols-3 gap-8 mb-10 text-center">
          {[
            ["1B", "Total Supply", "text-neonPink"],
            ["$0.0005", "Initial Price", "text-neonBlue"],
            ["$0.5M", "Initial Market Cap", "text-neonPurple"],
          ].map(([value, label, color]) => (
            <div key={label} className="p-4">
              <div className={`text-4xl font-bold mb-2 ${color}`}>{value}</div>
              <p className="text-gray-400">{label}</p>
            </div>
          ))}
        </div>

        {/* === Chart Section === */}
        <div className="mb-10">
          <h2 className="text-2xl font-bold mb-6 text-center text-neonBlue">
            Token Distribution
          </h2>
          <p className="text-center text-gray-400 mb-6 max-w-2xl mx-auto">
            The NEXORA token distribution ensures a sustainable economy by
            aligning incentives between investors, developers, partners,
            and the broader community.
          </p>
          <div className="h-80 w-full">
            <canvas ref={chartRef}></canvas>
          </div>
        </div>

        {/* === Allocation Table === */}
        <div>
          <h2 className="text-2xl font-bold mb-6 text-center text-neonPink">
            Detailed Allocation
          </h2>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="text-left border-b border-gray-700">
                  <th className="pb-2">Category</th>
                  <th className="pb-2">Percentage</th>
                  <th className="pb-2">Tokens</th>
                  <th className="pb-2">Vesting</th>
                </tr>
              </thead>
              <tbody>
                {[
                  ["Public Sale", "30%", "300,000,000", "10% at TGE, 3-month cliff, 15 months linear"],
                  ["Team", "15%", "150,000,000", "12-month cliff, 24 months linear"],
                  ["Advisors", "5%", "50,000,000", "6-month cliff, 18 months linear"],
                  ["Community", "25%", "250,000,000", "Monthly distribution over 36 months"],
                  ["Ecosystem", "12%", "120,000,000", "10% at TGE, 36 months linear"],
                  ["Liquidity", "8%", "80,000,000", "100% at TGE, locked 12 months"],
                  ["DAO Reserve", "5%", "50,000,000", "Governed by the DAO for strategic initiatives"],
                ].map(([cat, perc, tokens, vesting]) => (
                  <tr key={cat} className="border-b border-gray-800 hover:bg-gray-800/50 transition-all">
                    <td className="py-3">{cat}</td>
                    <td>{perc}</td>
                    <td>{tokens}</td>
                    <td>{vesting}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* === Token Utility === */}
      <div className="bg-gray-900/80 backdrop-blur-lg rounded-xl p-8 border border-neonBlue/40 shadow-md shadow-neonBlue/10">
        <h2 className="text-3xl font-bold mb-8 text-center text-transparent bg-clip-text bg-gradient-to-r from-neonPurple to-neonPink">
          Token Utility
        </h2>
        <p className="text-center text-gray-400 mb-10 max-w-3xl mx-auto">
          NEXORA serves as the cornerstone of a decentralized ecosystem bridging
          education, innovation, and social impact — driving tangible value
          for both users and the broader society.
        </p>
        <div className="grid md:grid-cols-3 gap-6">
          {[
            {
              title: "Governance & Voting",
              color: "neonPink",
              desc: "Holders actively participate in NEXORA DAO proposals, influencing strategic partnerships and community-driven initiatives.",
            },
            {
              title: "Access & Utility",
              color: "neonBlue",
              desc: "Stake NEXORA to access exclusive tools, educational content, partner rewards, and future token launches.",
            },
            {
              title: "Rewards & Incentives",
              color: "neonPurple",
              desc: "Earn NEXORA tokens by contributing knowledge, mentoring, or engaging in NEXORA social impact programs.",
            },
          ].map((item) => (
            <div
              key={item.title}
              className={`p-6 border border-${item.color}/40 rounded-lg text-center hover:shadow-md hover:shadow-${item.color}/20 transition-all`}
            >
              <h3 className={`text-xl font-bold mb-3 text-${item.color}`}>
                {item.title}
              </h3>
              <p className="text-gray-300">{item.desc}</p>
            </div>
          ))}
        </div>
      </div>

      {/* === CTA === */}
      <div className="text-center mt-20">
        <h2 className="text-3xl font-bold mb-4 text-transparent bg-clip-text bg-gradient-to-r from-neonPurple to-neonBlue">
          A Token with Real-World Impact
        </h2>
        <p className="text-gray-400 mb-6 max-w-2xl mx-auto">
          NEXORA is not just another crypto asset — it’s a tool for education,
          empowerment, and inclusive growth, building a better future
          for the next generation.
        </p>
        <a
          href="/roadmap"
          className="inline-block px-8 py-3 bg-gradient-to-r from-neonPurple to-neonPink rounded-lg font-bold hover:opacity-90 transition-all"
        >
          Explore Our Roadmap
        </a>
      </div>
    </div>
  );
};

export default Tokenomics;
